import React, { ReactElement, useEffect, useState } from 'react';
import { BarLoader } from 'react-spinners';
import {
  useGetGiftTypesHistoryMutation,
  useGetGiftTypesMutation,
} from '../../../../services/api/apiProvider';
import {
  ResponseGift,
  ResponseGiftCategory,
} from '../../../../services/api/models/response/responseGift';
import { GiftItem } from './GiftItem';
import './GiftTab.scss';
import { SlideTab, TabItem } from '../SlideTab/SlideTab';

interface GiftTabProps {
  giftCategory: ResponseGiftCategory;
  tabKey: string | null;
}

const PRICE_RANGES = [
  {
    name: '履歴',
    value: {
      isHistory: true,
    },
  },
  {
    name: 'すべて',
    value: {},
  },
  {
    name: '1~19',
    value: {
      min: 1,
      max: 19,
    },
  },
  {
    name: '20~99',
    value: {
      min: 20,
      max: 99,
    },
  },
  {
    name: '100~299',
    value: {
      min: 100,
      max: 299,
    },
  },
  {
    name: '300~999',
    value: {
      min: 300,
      max: 999,
    },
  },
  {
    name: '1000~4999',
    value: {
      min: 1000,
      max: 4999,
    },
  },
  {
    name: '5000~9999',
    value: {
      min: 5000,
      max: 9999,
    },
  },
  {
    name: '10000~',
    value: {
      min: 10000,
    },
  },
];

type FilterValue = (typeof PRICE_RANGES)[number]['value'];

export const ClassicGiftTab = (props: GiftTabProps): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [giftsData, setGiftsData] = useState<ResponseGift[]>([]);
  const [giftHistoryData, setGiftHistoryData] = useState<ResponseGift[]>([]);
  const [filterData, setFilterData] = useState<ResponseGift[] | null>(null);
  const [filterValue, setFilterValue] = useState<FilterValue>();

  const [getGiftTypes] = useGetGiftTypesMutation();
  const [getGiftTypesHistory] = useGetGiftTypesHistoryMutation();

  const data = filterValue?.isHistory
    ? giftHistoryData
    : filterData || giftsData;
  const isSelectedTab = props.giftCategory.id === props.tabKey;

  useEffect(() => {
    setLoading(true);
    getGiftTypes({
      giftCategoryId: props.giftCategory.id,
    })
      .unwrap()
      .then((data) => {
        setGiftsData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const fetchGiftHistory = () => {
    setLoading(true);
    getGiftTypesHistory({
      giftCategoryId: props.giftCategory.id,
      numInPage: 100,
    })
      .unwrap()
      .then((data) => {
        setGiftHistoryData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!filterValue?.isHistory || !isSelectedTab) return;
    fetchGiftHistory();
  }, [filterValue?.isHistory, isSelectedTab]);

  const handleFilterPrice = (filterItem: TabItem) => {
    const filter = filterItem.value as FilterValue;
    setFilterValue(filter);
    if (filter.isHistory) {
      return;
    }
    if (!filter.max && !filter.min) {
      setFilterData(null);
      return;
    }
    setFilterData(() => {
      return giftsData.filter((data) => {
        if (filter.max && data.amount > filter.max) {
          return false;
        }
        if (filter.min && data.amount < filter.min) {
          return false;
        }
        return true;
      });
    });
  };

  return (
    <div className="gift-tab">
      {loading && (
        <div className="gift-tab__loader">
          <BarLoader color={'#0275d8'} width="100%"></BarLoader>
        </div>
      )}

      <div className="gift-tab__body" style={{ paddingBottom: '44px' }}>
        {!!data.length && (
          <div className="list-gift">
            {data.map((gift) => (
              <GiftItem key={gift.id} gift={gift} />
            ))}
          </div>
        )}
        {(!!giftsData.length || !!filterData?.length) && (
          <>
            {/* Gift filter*/}
            {!!giftsData.length && (
              <SlideTab
                items={PRICE_RANGES}
                className="gift-tab__filter"
                onSelect={handleFilterPrice}
                defaultIndex={1}
              />
            )}
            {!data.length && !loading && (
              <p className="gift-tab__empty-text">
                該当するギフトがありません。
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
